// src/components/workflow/WorkflowTimeline.tsx
import React from 'react';
import { CheckCircle, XCircle, Clock, Send, Calendar, Flag, Play, Archive, ArrowRight, RotateCcw } from 'lucide-react';
import { StateTransitionLog } from '../../types';

interface Props {
    history: StateTransitionLog[];
    loading: boolean;
}

const STATE_ICONS: Record<string, { icon: React.ReactNode; color: string }> = {
    draf: { icon: <Clock className="w-3.5 h-3.5" />, color: 'bg-gray-400' },
    diajukan: { icon: <Send className="w-3.5 h-3.5" />, color: 'bg-blue-500' },
    validasi_l1: { icon: <CheckCircle className="w-3.5 h-3.5" />, color: 'bg-cyan-500' },
    ditolak_l1: { icon: <XCircle className="w-3.5 h-3.5" />, color: 'bg-red-500' },
    validasi_l2: { icon: <CheckCircle className="w-3.5 h-3.5" />, color: 'bg-green-500' },
    ditolak_l2: { icon: <XCircle className="w-3.5 h-3.5" />, color: 'bg-red-500' },
    antrean_rapim: { icon: <Calendar className="w-3.5 h-3.5" />, color: 'bg-orange-500' },
    diputuskan: { icon: <Flag className="w-3.5 h-3.5" />, color: 'bg-blue-500' },
    berlangsung: { icon: <Play className="w-3.5 h-3.5" />, color: 'bg-yellow-500' },
    selesai: { icon: <CheckCircle className="w-3.5 h-3.5" />, color: 'bg-emerald-500' },
    diarsipkan: { icon: <Archive className="w-3.5 h-3.5" />, color: 'bg-gray-500' },
    // special icon for looping back
    kembali_rapim: { icon: <RotateCcw className="w-3.5 h-3.5" />, color: 'bg-red-500' },
};

const STATUS_LABELS: Record<string, string> = {
    draf: 'Draft',
    diajukan: 'Diajukan',
    validasi_l1: 'Validasi Level 1',
    ditolak_l1: 'Ditolak Level 1',
    validasi_l2: 'Validasi Level 2',
    ditolak_l2: 'Ditolak Level 2',
    antrean_rapim: 'Dalam Antrian RAPIM',
    diputuskan: 'Keputusan RAPIM',
    berlangsung: 'Dalam Pengerjaan',
    selesai: 'Selesai',
    diarsipkan: 'Diarsipkan',
};

const WorkflowTimeline: React.FC<Props> = ({ history, loading }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-main" />
            </div>
        );
    }

    if (history.length === 0) {
        return (
            <p className="text-sm text-neutral-text-secondary text-center py-6">
                Belum ada riwayat perubahan status
            </p>
        );
    }

    return (
        <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-neutral-border" />
            <div className="space-y-5">
                {history.map((item, i) => {
                    const isKembaliRapim = item.from_state === 'berlangsung' && item.to_state === 'antrean_rapim';
                    const toConfig = isKembaliRapim ? STATE_ICONS['kembali_rapim'] : (STATE_ICONS[item.to_state] || { icon: <ArrowRight className="w-3.5 h-3.5" />, color: 'bg-gray-400' });
                    const isReject = item.to_state.includes('rejected') || isKembaliRapim;

                    return (
                        <div key={item.id || i} className="relative pl-10">
                            <div className={`absolute left-2.5 w-4 h-4 rounded-full flex items-center justify-center text-white ${toConfig.color} ${i === 0 ? 'ring-2 ring-offset-2 ring-teal-main/30' : ''}`}>
                                {toConfig.icon}
                            </div>
                            <div className="bg-white border border-neutral-border rounded-xl p-3 hover:shadow-sm transition-shadow">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        {item.from_state && (
                                            <>
                                                <span className="text-xs font-medium text-neutral-text-secondary">
                                                    {STATUS_LABELS[item.from_state] || item.from_state}
                                                </span>
                                                <ArrowRight className="w-3 h-3 text-neutral-text-secondary" />
                                            </>
                                        )}
                                        <span className={`text-xs font-bold ${isReject ? 'text-danger' : 'text-neutral-text-primary'}`}>
                                            {isKembaliRapim ? 'Dikembalikan ke RAPIM' : (STATUS_LABELS[item.to_state] || item.to_state)}
                                        </span>
                                    </div>
                                    <span className="text-xs text-neutral-text-secondary">
                                        {item.created_at ? new Date(item.created_at).toLocaleString('id-ID', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                        }) : ''}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-neutral-text-secondary">
                                    <span>oleh <strong className="text-neutral-text-primary">{item.actor?.name || 'System'}</strong></span>
                                    {item.actor_role && (
                                        <span className="px-1.5 py-0.5 bg-teal-main/5 text-teal-main rounded font-medium">
                                            {item.actor_role}
                                        </span>
                                    )}
                                </div>
                                {item.notes && (
                                    <div className={`mt-2 text-xs px-3 py-2 rounded-lg ${isReject ? 'bg-danger/5 text-danger border border-danger/10' : 'bg-neutral-bg text-neutral-text-secondary'}`}>
                                        {item.notes}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WorkflowTimeline;
